/**
 * FINANCE PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Tabbed dashboard: Budget | Cash Flow | Net Worth | Investments | Accounts | Transactions
 */

import React, { useState, useRef, useCallback } from 'react';
import { Screen, Button, Tabs } from '../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../design/tokens';
import { Alert } from '../components/ui';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';
import AccountsList, { AccountsListRef } from '../components/AccountsList';
import TransactionsList, { TransactionsListRef } from '../components/TransactionsList';
import PlaidLinkComponent from '../components/PlaidLink';
import { FinanceSummary, BudgetDashboard, CashFlowView, NetWorthView, InvestmentPortfolio, BudgetSetup, VehiclesView } from '../components/finance';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'budget', label: 'Budget' },
  { id: 'cashflow', label: 'Cash Flow' },
  { id: 'networth', label: 'Net Worth' },
  { id: 'investments', label: 'Investments' },
  { id: 'vehicles', label: 'Assets' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'transactions', label: 'Transactions' },
];

const FinancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showBudgetSetup, setShowBudgetSetup] = useState(false);
  const [budgetKey, setBudgetKey] = useState(0);
  const accountsListRef = useRef<AccountsListRef>(null);
  const transactionsListRef = useRef<TransactionsListRef>(null);

  // Apple Card CSV upload
  const [appleCardUploading, setAppleCardUploading] = useState(false);
  const appleCardFileRef = useRef<HTMLInputElement>(null);

  const handlePlaidSuccess = async (accounts: any[]) => {
    setError(null);
    setSuccess(`Successfully connected ${accounts.length} account(s)! Syncing transactions...`);
    accountsListRef.current?.refresh();
    transactionsListRef.current?.refresh();
  };

  const handlePlaidError = (err: any) => {
    const errorMessage = err.response?.data?.detail || err.message || 'Failed to connect bank accounts';
    setError(errorMessage);
  };

  const handleAppleCardUpload = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }
    setAppleCardUploading(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/api/apple-card/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = response.data;
      setSuccess(`Apple Card: ${r.created} new, ${r.updated} updated transactions imported`);
      accountsListRef.current?.refresh();
      transactionsListRef.current?.refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to import Apple Card CSV');
    } finally {
      setAppleCardUploading(false);
      if (appleCardFileRef.current) appleCardFileRef.current.value = '';
    }
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <FinanceSummary />;
      case 'budget':
        return <BudgetDashboard key={budgetKey} />;
      case 'cashflow':
        return <CashFlowView />;
      case 'networth':
        return <NetWorthView />;
      case 'investments':
        return <InvestmentPortfolio />;
      case 'vehicles':
        return <VehiclesView />;
      case 'accounts':
        return <AccountsList ref={accountsListRef} />;
      case 'transactions':
        return <TransactionsList ref={transactionsListRef} />;
      default:
        return null;
    }
  };

  return (
    <Screen>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
            }}>Finance</h1>
          </div>
          <div style={{ display: 'flex', gap: SPACING[8], alignItems: 'center' }}>
            {activeTab === 'budget' && (
              <Button variant="ghost" onClick={() => setShowBudgetSetup(true)}>
                Setup
              </Button>
            )}
            <input
              ref={appleCardFileRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAppleCardUpload(file);
              }}
            />
            <Button
              variant="secondary"
              onClick={() => appleCardFileRef.current?.click()}
              disabled={appleCardUploading}
              leftIcon={<ArrowUpTrayIcon style={{ width: '16px', height: '16px' }} />}
            >
              {appleCardUploading ? 'Importing...' : 'Apple Card CSV'}
            </Button>
            <PlaidLinkComponent
              onSuccess={handlePlaidSuccess}
              onError={handlePlaidError}
              buttonText="Connect Bank"
              buttonVariant="secondary"
              disabled={false}
            />
            <PlaidLinkComponent
              onSuccess={handlePlaidSuccess}
              onError={handlePlaidError}
              buttonText="Authorize Chase Investments"
              buttonVariant="primary"
              updateAccountId={22}
            />
          </div>
        </div>

        {/* Alerts */}
        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Tabs */}
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

        {/* Tab content */}
        {renderTab()}
      </div>

      {/* Budget Setup Modal */}
      <BudgetSetup
        isOpen={showBudgetSetup}
        onClose={() => setShowBudgetSetup(false)}
        onSaved={() => setBudgetKey(k => k + 1)}
      />
    </Screen>
  );
};

export default FinancePage;
